import { addError, logger } from "@wayflyer/blackbox-fe";
import type { NavIconOverlayProps, NavigationProps, NavItemButton, NavItemLink, NavSection } from "@wayflyer/flyui";
import { NavIconOverlay, ProductLogo, useRiveLoaderQuery, useToast } from "@wayflyer/flyui";
import {
  IconBank20Line,
  IconCity20Line,
  IconCreditCards20Line,
  IconEmptyPage20Line,
  IconGift20Line,
  IconGraphUp20Line,
  IconHeadsetHelp20Line,
  IconHome20Line,
  IconLogOut20Line,
  IconLotOfCash20Line,
  IconOpenBook20Line,
  IconReports20Line,
  IconSettings20Line,
  IconUserCircle20Line,
  IconWrench20Line,
} from "@wayflyer/flyui-icons/20/line";
import { BaseSchemas } from "@wayflyer/api-types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { COMPANY_ROLE_MESSAGES, useErrorModal, useLocalStorage } from "shared-ui";

import { useJoinAnalyticsPilotMutation } from "../../../analytics-pilot/queries";
import { useIntercomLoaded } from "../intercom";

import { getGlobalActionsSection, getLaunchReturnPath } from "./global-actions-section";
import { resolveCustomerNavPaths } from "./customer-nav-paths";
import { getNavItemNameFromId } from "./nav-item-strings";
import {
  CHECKOUT_NAV_ITEM_ID,
  getNavPlacement,
  isNavigableNavItem,
  needsPathPrefixMatching,
  type NavSection as NavLayoutSection,
} from "./nav-layout";
import { useCheckoutNavigationItem } from "./use-checkout-navigation-item";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}

type RiveData = NavIconOverlayProps["riveData"];
type IconRiveConfig = { src: string; gradient?: boolean };

/**
 * Every icon the backend can send, except the empty one it sends for items that have no icon.
 *
 * Derived from the generated union rather than hand-listed, so adding a `NavItemIcon` member
 * backend-side fails the build here until ICON_RIVE_MAP and getIconComponent handle it. The
 * previous hand-maintained list made that claim but could not keep it: it was compared against a
 * `string`, so eight backend icons drifted out of it unnoticed.
 */
type NavIconKey = Exclude<BaseSchemas.NavItemIcon, "">;

/**
 * Maps every NavIconKey to its Rive animation config.
 *
 * `Record<NavIconKey, ...>` is the guard: a new backend icon cannot compile until it has an entry.
 * If a top-level nav icon is nonetheless missing at runtime (a backend deploy landing ahead of the
 * frontend, before schemas are regenerated), Rive overlays are disabled for ALL items rather than
 * mixing animated and static ones.
 */
const ICON_RIVE_MAP: Record<NavIconKey, IconRiveConfig> = {
  home: { src: "/fly-assets/animations/icon_home-v4.riv" },
  bank: { src: "/fly-assets/animations/icon_a-lot-of-cash-v4.riv" },
  banking: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  account: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  payments: { src: "/fly-assets/animations/icon_wallet-v4.riv" },
  cards: { src: "/fly-assets/animations/icon_credit-cards-v4.riv" },
  transactions: { src: "/fly-assets/animations/icon_data-transfer-up-v6.riv" },
  "line-chart": { src: "/fly-assets/animations/icon_report-v4.riv" },
  documents: { src: "/fly-assets/animations/icon_multy-pages-v4.riv" },
  share: { src: "/fly-assets/animations/icon_gift-v4.riv" },
  connections: { src: "/fly-assets/animations/icon_wrech-v4.riv" },
  internal: { src: "/fly-assets/animations/icon_wrench-v4.riv" },
  intelligence: { src: "/fly-assets/animations/icon_intelligence-v7.riv", gradient: true },
};

const CUSTOMER_NAV_RIVE_SRCS = [...new Set(Object.values(ICON_RIVE_MAP).map(({ src }) => src))];

function wrapIconWithOverlay(icon: ReactNode, iconKey: NavIconKey, riveData: RiveData | undefined): ReactNode {
  const config = ICON_RIVE_MAP[iconKey];
  if (!riveData?.assets.has(config.src)) {
    return icon;
  }
  return (
    <NavIconOverlay riveSrc={config.src} riveData={riveData} gradient={config.gradient}>
      {icon}
    </NavIconOverlay>
  );
}

function getIconComponent(icon: NavIconKey, riveData?: RiveData): ReactNode {
  let staticIcon: ReactNode;
  switch (icon) {
    case "home":
      staticIcon = <IconHome20Line />;
      break;
    case "bank":
      staticIcon = <IconLotOfCash20Line />;
      break;
    case "line-chart":
      staticIcon = <IconReports20Line />;
      break;
    case "connections":
      staticIcon = <IconWrench20Line />;
      break;
    case "banking":
      staticIcon = <IconCity20Line />;
      break;
    case "internal":
      staticIcon = <IconWrench20Line />;
      break;
    case "share":
      staticIcon = <IconGift20Line />;
      break;
    case "account":
      staticIcon = <IconBank20Line />;
      break;
    case "payments":
      staticIcon = <IconGraphUp20Line />;
      break;
    case "cards":
      staticIcon = <IconCreditCards20Line />;
      break;
    case "transactions":
      staticIcon = <IconOpenBook20Line />;
      break;
    case "documents":
      staticIcon = <IconEmptyPage20Line />;
      break;
    case "intelligence":
      staticIcon = <ProductLogo.Intelligence />;
      break;
  }
  return wrapIconWithOverlay(staticIcon, icon, riveData);
}

const NAV_ICON_KEY_SET: ReadonlySet<string> = new Set(Object.keys(ICON_RIVE_MAP));

// Warn at most once per key. The type guard cannot catch a backend icon that ships before schemas
// are regenerated, so the check stays — but warning per render made this the single largest source
// of frontend log volume, ~220k lines a day (PFF-630).
const warnedIconKeys = new Set<string>();

function asNavIconKey(icon: BaseSchemas.NavItemIcon): NavIconKey | undefined {
  if (NAV_ICON_KEY_SET.has(icon)) {
    return icon as NavIconKey;
  }
  // "" is the backend's "no icon", not a gap.
  if (icon !== "" && !warnedIconKeys.has(icon)) {
    warnedIconKeys.add(icon);
    logger.warn(`Unknown nav icon key "${icon}" — add it to ICON_RIVE_MAP and getIconComponent`);
  }
  return undefined;
}

export const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

/**
 * The Accounts nav item is always rendered as a single link to the accounts
 * screen — individual accounts are never surfaced as sub-items.
 */
const isAccountsNavItem = (navItem: BaseSchemas.NavItem): boolean =>
  navItem.id.includes("accounts") || !!navItem.path?.includes("/accounts");

type CustomerNavItem = NavItemLink | NavSection;
// `NavGroup` is not re-exported from the flyui root barrel, so derive the
// grouped-sections shape from the public NavigationProps type.
type CustomerNavGroups = Extract<NonNullable<NavigationProps["sections"]>, { groups: unknown }>["groups"];

/**
 * Groups the top-level nav items into the primary group and the bottom
 * "Products" group. Order and section come from the declarative {@link NAV_LAYOUT}
 * config rather than the server's ordering. Items the config doesn't recognise
 * fall back to the end of the main group so nothing silently disappears.
 */
export function buildNavGroups(items: CustomerNavItem[], productsLabel: string): CustomerNavGroups {
  const sectionOf = (item: CustomerNavItem): NavLayoutSection => getNavPlacement(item.id)?.section ?? "main";
  const orderOf = (item: CustomerNavItem): number => getNavPlacement(item.id)?.order ?? Number.MAX_SAFE_INTEGER;
  const itemsInSection = (section: NavLayoutSection): CustomerNavItem[] =>
    items.filter((item) => sectionOf(item) === section).sort((a, b) => orderOf(a) - orderOf(b));

  const productItems = itemsInSection("products");

  return [
    { id: "main", items: itemsInSection("main") },
    ...(productItems.length > 0 ? [{ id: "products", label: productsLabel, items: productItems }] : []),
  ];
}

export const addCheckoutNavigationItem = (
  items: CustomerNavItem[],
  checkoutItem: NavItemLink | undefined
): CustomerNavItem[] => (checkoutItem ? [...items, checkoutItem] : items);

export const getCheckoutNotificationBubble = (
  checkoutItem: NavItemLink | undefined
): { navItemId: string; label: string } | undefined =>
  checkoutItem
    ? {
        navItemId: CHECKOUT_NAV_ITEM_ID,
        label: "New",
      }
    : undefined;

export const useNavigationItems = ({ appConfig }: { appConfig: BaseSchemas.CustomerAppConfig }): NavigationProps => {
  const intl = useIntl();
  const location = useLocation();
  const { customerDomain } = appConfig;
  const navItems = resolveCustomerNavPaths(appConfig.navItems ?? [], customerDomain);
  // Rive overlays are gated behind the "animated-side-nav-icons" feature flag.
  // Disable overlays if any top-level icon lacks a Rive mapping to avoid a mix
  // of animated and static icons.
  const animatedIconsEnabled = useFeatureFlagEnabled("animated-side-nav-icons");
  const analyticsPilotEnabled = useFeatureFlagEnabled("conjura-analytics-pilot");
  const [analyticsPilotClicked, setAnalyticsPilotClicked] = useLocalStorage<boolean>(
    `analytics-pilot-clicked-${customerDomain}`,
    false,
    true
  );
  const { mutate: joinPilot } = useJoinAnalyticsPilotMutation(customerDomain);
  const allIconsCovered = (navItems ?? []).every((item) => item.icon in ICON_RIVE_MAP);
  const riveResult = useRiveLoaderQuery(CUSTOMER_NAV_RIVE_SRCS);
  const riveData = animatedIconsEnabled && allIconsCovered ? riveResult.data : undefined;
  const alternativePathsMap = {};
  const [messageCount, setMessageCount] = useState(0);
  const messengerOpen = useRef(false);
  const isIntercomLoaded = useIntercomLoaded();
  const checkoutNavigationItem = useCheckoutNavigationItem({ customerDomain });

  useEffect(() => {
    if (isIntercomLoaded) {
      const handleUnreadCountChange = (unreadCount: number) => {
        setMessageCount(unreadCount);
      };
      const handleHide = () => {
        messengerOpen.current = false;
      };
      const handleShow = () => {
        messengerOpen.current = true;
      };

      window.Intercom("onUnreadCountChange", handleUnreadCountChange);
      window.Intercom("onHide", handleHide);
      window.Intercom("onShow", handleShow);

      return () => {
        // Clean up listeners
        window.Intercom("off", "onUnreadCountChange", handleUnreadCountChange);
        window.Intercom("off", "onHide", handleHide);
        window.Intercom("off", "onShow", handleShow);
      };
    }
  }, [isIntercomLoaded]);

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    // Some nav items (e.g. Accounts, Cards) must stay highlighted on sibling
    // sub-pages outside their own router subtree (e.g. /cards/create, an
    // individual account). Registering their own path as an alternative makes
    // getIsRouteSelected's prefix match cover those routes.
    if (needsPathPrefixMatching(navItem.id) && navItem.path) {
      alternativePathsMap[navItem.path] = [...(alternativePathsMap[navItem.path] ?? []), navItem.path];
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): NavSection["items"] | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    // Sub-items are text-only by design, so the backend's `icon` is deliberately ignored here.
    return navItems.map(({ id, path }) => ({
      id,
      href: path,
      label: getNavItemNameFromId(intl, id),
    }));
  }

  const userProfile: NavigationProps["userProfile"] = {
    name: appConfig.userName,
    email: appConfig.userEmail,
    role: appConfig.userRole ? intl.formatMessage(COMPANY_ROLE_MESSAGES[appConfig.userRole]) : undefined,
    // Empty until staff Slack images land; nav falls back to initials.
    profileImageUrl: appConfig.profileImgUrl || undefined,
  };

  const toaster = useToast();
  const { openErrorModal } = useErrorModal();
  const isNavItemLink = (item: NavItemLink | NavSection): item is NavItemLink => {
    return !("items" in item);
  };
  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    const iconKey = asNavIconKey(navItem.icon);
    const icon = iconKey ? getIconComponent(iconKey, riveData) : undefined;
    const label = getNavItemNameFromId(intl, navItem.id);
    // Some top-level items (e.g. Performance) are themselves links in addition
    // to grouping their sub-items — see NAVIGABLE_NAV_IDS in nav-layout.
    const navigableSection = isNavigableNavItem(id);

    // Accounts is always a single link to the accounts screen; we never surface
    // individual accounts as sub-items. Active state on /accounts sub-pages is
    // handled via the alternativePaths registration above.
    if (isAccountsNavItem(navItem)) {
      return {
        id,
        icon,
        label,
        href: navItem.path,
      };
    }

    const items = getClientSubNavItems(navItem.subItems);

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
      };
    }
  };

  const userSection: NavigationProps["userSection"] = {
    label: appConfig.userName,
    id: "user-section",
    icon: null,
    items: [],
    sections: [
      {
        id: "user-actions",
        items: [
          {
            id: "profile",
            label: intl.formatMessage({ id: "Menu: Profile", defaultMessage: "Profile" }),
            // User-scoped, mounted domain-less so it's reachable before a company exists.
            href: "/settings/account",
            icon: <IconUserCircle20Line />,
          },
          ...(navItems ?? [])
            .map(toClientNavItem)
            .filter(isNavItemLink)
            .filter((item) => getNavPlacement(item.id)?.section === "user"),
          ...(window?.Optanon?.ToggleInfoDisplay
            ? [
                {
                  id: "privacy-settings",
                  label: intl.formatMessage({
                    id: "Privacy settings",
                    defaultMessage: "Privacy preferences",
                  }),
                  onClick: () => window.Optanon.ToggleInfoDisplay(),
                  icon: <IconSettings20Line />,
                },
              ]
            : []),
        ],
      },
      {
        id: "auth-actions",
        items: [
          {
            id: "sign-out",
            label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
            critical: true,
            href: "/authn/logout",
            reloadDocument: true,
            icon: <IconLogOut20Line />,
          },
        ],
      },
    ],
  };

  const clientNavItems = addCheckoutNavigationItem((navItems ?? []).map(toClientNavItem), checkoutNavigationItem);

  // The company profile menu surfaces the new IA pages. We list only pages that
  // have a built screen (Profile, Documents, Users); the design's Settings/Rules
  // entries are deferred until those pages exist. Connections is intentionally NOT
  // here — it lives in the side-nav (just below Intelligence, see clientNavItems
  // above). Invite moved to the Users page's primary action (PFF-557).
  const companyProfileMenuItems = [
    {
      id: "company-profile",
      label: intl.formatMessage({ id: "Menu: Company profile", defaultMessage: "Profile" }),
      href: `/${customerDomain}/settings/company`,
    },
    {
      id: "company-documents",
      label: intl.formatMessage({ id: "Menu: Company documents", defaultMessage: "Documents" }),
      href: `/${customerDomain}/settings/company/documents`,
    },
    {
      id: "company-users",
      label: intl.formatMessage({ id: "Menu: Company users", defaultMessage: "Users" }),
      href: `/${customerDomain}/settings/company/users`,
    },
  ];

  const companySection: NavigationProps["companySection"] =
    appConfig.customerName?.length > 0
      ? {
          label: appConfig.customerName,
          id: "company-section",
          icon: null,
          logoUrl: appConfig.companyLogoUrl || undefined,
          items: companyProfileMenuItems,
        }
      : undefined;

  const sections: NavigationProps["sections"] = {
    groups: buildNavGroups(
      clientNavItems,
      intl.formatMessage({ id: "products section title", defaultMessage: "Products" })
    ),
  };

  const infoItems = [
    ...(isIntercomLoaded
      ? [
          {
            id: "support",
            label: intl.formatMessage({ id: "support title", defaultMessage: "Support" }),
            onClick: () => {
              if ("Intercom" in window) {
                if (messengerOpen.current) {
                  window.Intercom("hide");
                } else {
                  window.Intercom("show");
                }
              }
            },
            icon: <IconHeadsetHelp20Line />,
          },
        ]
      : []),
  ] as Array<NavItemButton | NavItemLink>;

  const notificationBubbles: Array<{ navItemId: string; label: string; title?: string }> = [];
  if (messageCount > 0) {
    notificationBubbles.push({
      navItemId: "support",
      label: String(messageCount),
      title: `${messageCount} messages require your attention`,
    });
  }
  if ((navItems ?? []).some((item) => item.id === "intelligence page title")) {
    notificationBubbles.push({
      navItemId: "intelligence page title",
      label: "New",
    });
  }
  const checkoutNotificationBubble = getCheckoutNotificationBubble(checkoutNavigationItem);
  if (checkoutNotificationBubble) {
    notificationBubbles.push(checkoutNotificationBubble);
  }

  return {
    userProfile,
    userSection,
    companySection,
    globalSection: getGlobalActionsSection({
      intl,
      customerDomain,
      navItems,
      returnPath: getLaunchReturnPath({
        customerDomain,
        pathname: location.pathname,
        search: location.search,
        state: location.state,
      }),
    }),
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubbles: notificationBubbles.length > 0 ? notificationBubbles : undefined,
    alwaysVisibleItemIds: [],
    enableCollapse: appConfig.featureFlags?.enableCollapsingSideNav ?? false,
    topBanners:
      analyticsPilotEnabled && !analyticsPilotClicked
        ? [
            {
              id: "analytics-pilot-banner",
              message: intl.formatMessage({
                id: "Analytics pilot banner: message",
                defaultMessage: "Join to get early access to our new AI agent: Wayflyer Intelligence",
              }),
              action: {
                content: intl.formatMessage({
                  id: "Analytics pilot banner: cta",
                  defaultMessage: "Join Now",
                }),
                analyticsId: "analytics-pilot-join-banner",
                onAction: () => {
                  joinPilot(undefined, {
                    onSuccess: () => {
                      setAnalyticsPilotClicked(true);
                      toaster.positive(
                        intl.formatMessage({
                          id: "Analytics pilot banner: joined",
                          defaultMessage: "Thanks — we’ve received your details and will be in touch.",
                        })
                      );
                    },
                    onError: (error: unknown) => {
                      addError("Error joining analytics pilot");
                      openErrorModal({
                        variant: "submit-error",
                        error: new Error("Failed to join analytics pilot", { cause: error }),
                      });
                    },
                  });
                },
              },
              color: "black" as const,
              matchRoutes: ["/:customerDomain/performance/*"],
            },
          ]
        : undefined,
  };
};
